import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import {
  Loader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NotificationStack,
  NotificationType,
  RequiredItem,
} from "@transfr-inc/dashboard-components";
import {
  Button,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
  Input,
  Textarea,
} from "@transfr-inc/dashboard-components/forms";
import { useNotificationManager } from "@transfr-inc/dashboard-components/hooks";

import { OrgFormToggle } from "@components/forms/org-editor";
import { WarningMessages } from "@transfr-inc/dashboard-components";
import { EditProgramDetailsSuccess } from "@components/utils/notification.constants";
import { services, toggles } from "@src/dependencies";
import { useEditWarningModal } from "@transfr-inc/dashboard-components/hooks";

import {
  EditLocation,
  awardTypes,
  getAwardTypeLabel,
  useSelectedAward,
} from "@transfr-inc/dashboard-components/curriculum";

import "./edit-program-details.modal.scss";

export function EditProgramDetailsModal({
  program,
  open,
  onCancel,
  onEditProgramDetailsHandler,
  showNotification,
  mappedAllCredentials,
  preventClose = true,
  courseCredentialsList,
}) {
  const { programsService } = services;
  const { isProfessionalLearningEnabled } = toggles;
  const [loading, setLoading] = useState();
  const {
    notifications,
    addNotifications,
    clearNotifications,
    removeNotification,
  } = useNotificationManager();

  const methods = useForm({
    criteriaMode: "all",
    mode: "onBlur",
    defaultValues: {
      ...program,
    },
  });

  const { handleSubmit, reset, formState, control, watch } = methods;
  const { errors, isValid } = formState;

  const awardTypeWatch = watch("awardType");

  const checkErrors = (formErrors) => {
    const messages = new Set(Object.values(formErrors).map((e) => e.message));

    clearNotifications();
    addNotifications(
      messages.values().map((message) => ({
        type: NotificationType.error,
        message,
        closable: false,
      })),
    );
  };

  const { selectedAward, selectedAwardType } = useSelectedAward(
    program,
    mappedAllCredentials,
    courseCredentialsList,
  );

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      checkErrors(errors);
    }
  }, [formState]);

  useEffect(() => {
    if (!open) {
      reset();
    } else if (program) {
      const formData = {
        ...program,
        awardIds: selectedAward,
        awardType: selectedAwardType,
      };
      reset(formData);
    }
  }, [open, reset, program, selectedAward, selectedAwardType]);

  const onCloseHandler = () => {
    onCancel && onCancel();
    clearNotifications();
    reset();
  };

  const submitForm = async (values) => {
    clearNotifications();

    try {
      const formattedData = {
        editedFrom: EditLocation.EditProgramDetailsModal.id,
        course: {
          id: program.id,
          title: values.title,
          description: values.description,
          prerequisitesEnabled: values.prerequisitesEnabled ? 1 : 0,
          professionalLearning: values.professionalLearning ? 1 : 0,
          awardIds: [
            {
              id: values.awardIds?.id,
              type: values.awardType,
            },
          ],
        },
      };

      const updateProgramData =
        await programsService.updateCourseMetadata(formattedData);
      if (showNotification) {
        showNotification({
          type: NotificationType.success,
          message: EditProgramDetailsSuccess(updateProgramData),
          icon: "circle-check",
        });
      } else {
        addNotifications([
          {
            type: NotificationType.success,
            message: "Program edited successfully",
            closable: true,
          },
        ]);
      }
      onCloseHandler();
      onEditProgramDetailsHandler();
    } catch (error) {
      const errorMessage =
        error?.data?.detail?.[0]?.msg ||
        error?.data?.detail ||
        "Something went wrong";
      addNotifications([
        {
          type: NotificationType.error,
          message: errorMessage,
          closable: true,
        },
      ]);
    } finally {
      setLoading();
    }

    return true;
  };

  const editProgramDetailsHandler = () => {
    setLoading(true);
    handleSubmit(submitForm)();
  };

  const [warningEditModal, openWarningEditModal] = useEditWarningModal({
    title: WarningMessages.program.title,
    message: WarningMessages.program.edit,
    buttons: [
      {
        id: "no",
        label: "No, Cancel",
        handler: onCancel,
      },
      {
        id: "yes",
        label: "Yes, I'm Sure",
        handler: editProgramDetailsHandler,
        primary: true,
      },
    ],
  });

  const onEditProgramDetails = () => {
    if (program?.organizations?.length > 0) {
      openWarningEditModal();
    } else {
      editProgramDetailsHandler();
    }
  };

  return (
    <>
      <Modal
        modalClassName={"edit-program-details-modal"}
        open={open}
        onClose={onCloseHandler}
        preventClose={loading || preventClose}
      >
        {loading && <Loader overlay></Loader>}
        <ModalHeader
          className="blue-icon"
          icon={["fa-regular", "pen-to-square"]}
          title="Edit Program Details"
        ></ModalHeader>
        <ModalBody>
          <div className="validation-errors">
            {notifications?.length > 0 && (
              <NotificationStack
                notifications={notifications}
                onClose={removeNotification}
                animated
              />
            )}
          </div>
          <div className="text-with-info">
            <label>
              Required Fields<sup>*</sup>
            </label>
          </div>
          <FormProvider {...methods}>
            <form className="edit-program-form">
              <Controller
                control={control}
                name="title"
                defaultValue={program?.title || ""}
                rules={{
                  required: {
                    value: true,
                    message: "Required fields are missing.",
                  },
                  maxLength: {
                    value: 255,
                    message: "Title must be less than 100 characters.",
                  },
                }}
                render={({ field: { onBlur, onChange, value, ref } }) => {
                  return (
                    <Input
                      label={<RequiredItem text="Title" />}
                      type="text"
                      name="title"
                      errors={errors?.title}
                      ref={ref}
                      onBlur={onBlur}
                      onChange={onChange}
                      value={value}
                      required
                    />
                  );
                }}
              />
              <div className="description-input">
                <Controller
                  control={control}
                  defaultValue={program?.description}
                  name="description"
                  rules={{
                    required: {
                      value: false,
                      message: "Required fields are missing.",
                    },
                    maxLength: {
                      value: 500,
                      message: "Description must be less than 1000 characters.",
                    },
                  }}
                  render={({ field: { onBlur, onChange, value, ref } }) => {
                    return (
                      <Textarea
                        label={<h4>Description</h4>}
                        name="description"
                        errors={errors?.description}
                        ref={ref}
                        onBlur={onBlur}
                        onInputChange={onChange}
                        value={value}
                        required
                      />
                    );
                  }}
                />
              </div>
              <div className="awards-title-section">
                <div className="section-title">
                  <h4>Awards</h4>
                </div>
              </div>
              <div className="awards-section">
                <Controller
                  control={control}
                  name="awardType"
                  render={({ field: { onChange, value } }) => {
                    const selectedAwardLabel = getAwardTypeLabel(value);
                    return (
                      <Dropdown
                        className={"awards-type-dropdown"}
                        selected={value}
                        onOptionSelected={onChange}
                      >
                        <DropdownButton
                          placeholder={"Select Type..."}
                          clearEnabled
                        >
                          {selectedAwardLabel}
                        </DropdownButton>
                        <DropdownItems>
                          {awardTypes?.map((award) => (
                            <DropdownItem key={award.id} value={award.id}>
                              {award.label}
                            </DropdownItem>
                          ))}
                        </DropdownItems>
                      </Dropdown>
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="awardIds"
                  disabled={!awardTypeWatch}
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      className={"awards-dropdown"}
                      selected={value}
                      onOptionSelected={onChange}
                    >
                      <DropdownButton
                        placeholder={"Select a credential..."}
                        clearEnabled
                      >
                        {value?.name}
                      </DropdownButton>
                      <DropdownItems>
                        {mappedAllCredentials?.map((award) => (
                          <DropdownItem
                            key={award.id}
                            value={award}
                            disabled={award.disabled}
                          >
                            {award.name}
                          </DropdownItem>
                        ))}
                      </DropdownItems>
                    </Dropdown>
                  )}
                />
              </div>
              <OrgFormToggle
                name="prerequisitesEnabled"
                title="Prerequisites"
                infoText="Would you like to enable prerequisites?"
                defaultValue={program?.prerequisitesEnabled || false}
              ></OrgFormToggle>
              {isProfessionalLearningEnabled && (
                <OrgFormToggle
                  name="professionalLearning"
                  title="Basics"
                  infoText="Is this a Basics program?"
                  defaultValue={program?.professionalLearning ?? false}
                />
              )}
            </form>
          </FormProvider>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onCloseHandler}>Cancel</Button>
          <Button primary onClick={onEditProgramDetails} disabled={!isValid}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
      {warningEditModal}
    </>
  );
}
